import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useReducedMotion,
} from 'framer-motion';

const BALL_SIZE = 88;
const DODGE_RADIUS = 110;
const DODGE_COUNT_COOLDOWN_MS = 200;
const STEP_TRANSITION_MS = 150;
const RIGGED_DODGES = 2;
const AMBIENT_SPEED = 4.2;
const MAX_SPEED = 46;
const FLEE_FORCE = 0.96;

const CATCH_HINT = 'Place your mouse outside of the page window, then try to click.';

const RIGGED_HINTS = [
  'Almost... not.',
  'Nope. Too fast for you.',
  'One more fake-out.',
  'Still not.',
  'Last fake-out. Then you\'re in.',
];

type GateStep = 'verify' | 'password';

interface VerificationGateProps {
  accessPassword: string;
  onUnlock: () => void;
}

function randomVelocity(speed: number) {
  const angle = Math.random() * Math.PI * 2;
  return { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
}

const VerificationGateBackground: React.FC<{ preferStatic?: boolean }> = ({ preferStatic }) => {
  const [videoError, setVideoError] = useState(false);
  const showVideo = !preferStatic && !videoError;

  return (
    <div className="verify-gate-bg" aria-hidden="true">
      {showVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="verify-gate-bg-video"
          onError={() => setVideoError(true)}
        >
          <source src="/assets/scenery.mp4" type="video/mp4" />
        </video>
      )}
      <div className="verify-gate-bg-vignette" />
      <div className="verify-gate-bg-overlay" />
    </div>
  );
};

const VerificationGate: React.FC<VerificationGateProps> = ({ accessPassword, onUnlock }) => {
  const prefersReducedMotion = useReducedMotion();
  const [step, setStep] = useState<GateStep>('verify');
  const [hint, setHint] = useState(CATCH_HINT);
  const [chipSuccess, setChipSuccess] = useState(false);
  const [chipDodgeFlash, setChipDodgeFlash] = useState(false);
  const [fallbackClicks, setFallbackClicks] = useState(0);
  const [dodgeCount, setDodgeCount] = useState(0);

  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const playAreaRef = useRef<HTMLDivElement>(null);
  const dodgeFlashTimerRef = useRef<number | null>(null);
  const lastDodgeTimeRef = useRef(0);
  const dodgeCountRef = useRef(0);
  const cursorRef = useRef<{ x: number; y: number } | null>(null);
  const animatingRef = useRef(true);
  const velocityRef = useRef(randomVelocity(AMBIENT_SPEED));
  const positionRef = useRef({ x: 0, y: 0 });

  const ballX = useMotionValue(0);
  const ballY = useMotionValue(0);

  const isClickable = dodgeCount >= RIGGED_DODGES;

  useEffect(() => {
    dodgeCountRef.current = dodgeCount;
  }, [dodgeCount]);

  const getBounds = useCallback(() => {
    const area = playAreaRef.current;
    if (!area) {
      return { maxX: 0, maxY: 0 };
    }
    return {
      maxX: Math.max(0, area.clientWidth - BALL_SIZE),
      maxY: Math.max(0, area.clientHeight - BALL_SIZE),
    };
  }, []);

  const getRandomPosition = useCallback(() => {
    const { maxX, maxY } = getBounds();
    return {
      x: Math.floor(Math.random() * (maxX + 1)),
      y: Math.floor(Math.random() * (maxY + 1)),
    };
  }, [getBounds]);

  const getPositionFarFrom = useCallback(
    (fromX: number, fromY: number) => {
      const minSeparation = DODGE_RADIUS + BALL_SIZE * 0.75;
      for (let attempt = 0; attempt < 16; attempt += 1) {
        const pos = getRandomPosition();
        const centerX = pos.x + BALL_SIZE / 2;
        const centerY = pos.y + BALL_SIZE / 2;
        if (Math.hypot(centerX - fromX, centerY - fromY) >= minSeparation) {
          return pos;
        }
      }
      return getRandomPosition();
    },
    [getRandomPosition]
  );

  const teleportBallInstant = useCallback(
    (fromX: number, fromY: number) => {
      const pos = getPositionFarFrom(fromX, fromY);
      ballX.set(pos.x);
      ballY.set(pos.y);
      positionRef.current = pos;
    },
    [ballX, ballY, getPositionFarFrom]
  );

  const flashDodge = useCallback(() => {
    setChipDodgeFlash(true);
    if (dodgeFlashTimerRef.current !== null) {
      window.clearTimeout(dodgeFlashTimerRef.current);
    }
    dodgeFlashTimerRef.current = window.setTimeout(() => {
      setChipDodgeFlash(false);
      dodgeFlashTimerRef.current = null;
    }, 180);
  }, []);

  const registerDodge = useCallback(() => {
    const next = dodgeCountRef.current + 1;
    if (next > RIGGED_DODGES) {
      return;
    }

    if (next < RIGGED_DODGES) {
      setHint(RIGGED_HINTS[dodgeCountRef.current] ?? RIGGED_HINTS[RIGGED_HINTS.length - 1]);
      if (next === 1) {
        animatingRef.current = true;
        velocityRef.current = randomVelocity(AMBIENT_SPEED);
      }
    } else {
      setHint(CATCH_HINT);
      animatingRef.current = false;
      velocityRef.current = { x: 0, y: 0 };
    }

    dodgeCountRef.current = next;
    setDodgeCount(next);
    flashDodge();
  }, [flashDodge]);

  const registerDodgeRef = useRef(registerDodge);
  registerDodgeRef.current = registerDodge;

  const repositionBall = useCallback(() => {
    const pos = getRandomPosition();
    ballX.set(pos.x);
    ballY.set(pos.y);
    positionRef.current = pos;
    velocityRef.current = { x: 0, y: 0 };
  }, [ballX, ballY, getRandomPosition]);

  useLayoutEffect(() => {
    if (step !== 'verify' || prefersReducedMotion) {
      return;
    }
    repositionBall();
    animatingRef.current = false;
  }, [step, prefersReducedMotion, repositionBall]);

  useEffect(() => {
    if (step !== 'verify' || prefersReducedMotion) {
      return undefined;
    }

    const handleResize = () => {
      const { maxX, maxY } = getBounds();
      const clampedX = Math.min(Math.max(ballX.get(), 0), maxX);
      const clampedY = Math.min(Math.max(ballY.get(), 0), maxY);
      ballX.set(clampedX);
      ballY.set(clampedY);
      positionRef.current = { x: clampedX, y: clampedY };
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [step, prefersReducedMotion, ballX, ballY, getBounds]);

  useEffect(() => {
    if (isClickable && !chipSuccess) {
      setHint(CATCH_HINT);
    }
  }, [isClickable, chipSuccess]);

  useEffect(() => {
    if (isClickable) {
      animatingRef.current = false;
      velocityRef.current = { x: 0, y: 0 };
    }
  }, [isClickable]);

  useEffect(() => {
    if (chipSuccess) {
      animatingRef.current = false;
    }
  }, [chipSuccess]);

  useEffect(() => {
    return () => {
      if (dodgeFlashTimerRef.current !== null) {
        window.clearTimeout(dodgeFlashTimerRef.current);
      }
    };
  }, []);

  useAnimationFrame((_time, delta) => {
    if (prefersReducedMotion || step !== 'verify' || chipSuccess) {
      return;
    }

    if (dodgeCountRef.current >= RIGGED_DODGES) {
      return;
    }

    const { maxX, maxY } = getBounds();
    const dt = delta / 16.67;

    let bx = ballX.get();
    let by = ballY.get();
    let vx = velocityRef.current.x;
    let vy = velocityRef.current.y;

    const cursor = cursorRef.current;
    const ballCenterX = bx + BALL_SIZE / 2;
    const ballCenterY = by + BALL_SIZE / 2;

    if (cursor) {
      const dx = ballCenterX - cursor.x;
      const dy = ballCenterY - cursor.y;
      const distance = Math.hypot(dx, dy);

      if (distance < DODGE_RADIUS && distance > 0) {
        const fleeStrength = (DODGE_RADIUS - distance) / DODGE_RADIUS;
        const fleeX = dx / distance;
        const fleeY = dy / distance;
        vx += fleeX * fleeStrength * FLEE_FORCE * dt;
        vy += fleeY * fleeStrength * FLEE_FORCE * dt;
      }
    } else {
      vx *= 0.985;
      vy *= 0.985;
    }

    if (dodgeCountRef.current > 0) {
      const speed = Math.hypot(vx, vy);
      const minSpeed = AMBIENT_SPEED * 0.9;
      if (speed < minSpeed) {
        const scale = minSpeed / (speed || 1);
        vx *= scale;
        vy *= scale;
      }
      if (speed > MAX_SPEED) {
        vx = (vx / speed) * MAX_SPEED;
        vy = (vy / speed) * MAX_SPEED;
      }
    } else if (Math.hypot(vx, vy) > MAX_SPEED) {
      const speed = Math.hypot(vx, vy);
      vx = (vx / speed) * MAX_SPEED;
      vy = (vy / speed) * MAX_SPEED;
    }

    bx += vx * dt;
    by += vy * dt;

    if (bx <= 0) {
      bx = 0;
      vx = Math.abs(vx) * 0.98;
    }
    if (bx >= maxX) {
      bx = maxX;
      vx = -Math.abs(vx) * 0.98;
    }
    if (by <= 0) {
      by = 0;
      vy = Math.abs(vy) * 0.98;
    }
    if (by >= maxY) {
      by = maxY;
      vy = -Math.abs(vy) * 0.98;
    }

    velocityRef.current = { x: vx, y: vy };
    ballX.set(bx);
    ballY.set(by);
    positionRef.current = { x: bx, y: by };
  });

  const handlePlayAreaPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const area = playAreaRef.current;
    if (!area) {
      return;
    }

    const rect = area.getBoundingClientRect();
    cursorRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePlayAreaPointerLeave = () => {
    cursorRef.current = null;
  };

  const completeVerification = () => {
    setChipSuccess(true);
    setHint('');
    animatingRef.current = false;
    window.setTimeout(() => setStep('password'), STEP_TRANSITION_MS);
  };

  const handleSphereClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (chipSuccess) {
      return;
    }

    if (isClickable) {
      completeVerification();
      return;
    }

    const area = playAreaRef.current;
    if (!area) {
      return;
    }

    const rect = area.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;

    teleportBallInstant(pointerX, pointerY);

    const now = Date.now();
    if (now - lastDodgeTimeRef.current > DODGE_COUNT_COOLDOWN_MS) {
      lastDodgeTimeRef.current = now;
      registerDodgeRef.current();
    }
  };

  const handleReducedMotionVerify = () => {
    if (fallbackClicks < RIGGED_DODGES) {
      setHint(RIGGED_HINTS[fallbackClicks]);
      setFallbackClicks((count) => count + 1);
      if (fallbackClicks + 1 >= RIGGED_DODGES) {
        setHint(CATCH_HINT);
      }
      return;
    }
    completeVerification();
  };

  const handlePasswordSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (passwordInput.trim() === accessPassword) {
      setPasswordError('');
      setPasswordInput('');
      onUnlock();
      return;
    }
    setPasswordError('That password is not correct.');
  };

  if (step === 'verify') {
    const showPrimaryHint = dodgeCount === 0 || isClickable;

    return (
      <div className="verification-gate-root">
        <VerificationGateBackground preferStatic={prefersReducedMotion ?? false} />
        <div className="verification-shell verification-shell--puzzle">
        <div className="verify-overlay-header">
          <div className="password-badge">Private Portfolio</div>
          <h1 className="password-title verify-overlay-title">Prove you&apos;re human</h1>
          {!showPrimaryHint && (
            <p className="password-subtitle verify-overlay-subtitle">
              Hover steers it away. Keep clicking to finish your tries.
            </p>
          )}
        </div>

        {showPrimaryHint && (
          <p className="verify-primary-hint">{CATCH_HINT}</p>
        )}

        {prefersReducedMotion ? (
          <div className="verify-fallback-wrap">
            <button
              type="button"
              className="password-button verify-fallback-button"
              onClick={handleReducedMotionVerify}
            >
              I&apos;m human
            </button>
            {hint && !showPrimaryHint && <p className="verify-hint verify-hint--overlay">{hint}</p>}
          </div>
        ) : (
          <>
            <div
              ref={playAreaRef}
              className="verify-play-area verify-play-area--fullscreen"
              aria-label="Verification play area"
              onPointerMove={handlePlayAreaPointerMove}
              onPointerLeave={handlePlayAreaPointerLeave}
            >
              <motion.button
                type="button"
                className={`verify-sphere ${chipSuccess ? 'verify-sphere--success' : ''} ${chipDodgeFlash ? 'verify-sphere--dodge verify-sphere--pop' : ''} ${isClickable ? 'verify-sphere--catchable' : dodgeCount > 0 ? 'verify-sphere--drifting' : 'verify-sphere--waiting'}`}
                style={{ x: ballX, y: ballY, width: BALL_SIZE, height: BALL_SIZE }}
                onClick={handleSphereClick}
                disabled={chipSuccess}
                aria-label={
                  chipSuccess
                    ? 'Verified'
                    : isClickable
                      ? 'Click to verify'
                      : 'Runaway verification ball'
                }
              >
                <span className="verify-sphere-shine" aria-hidden="true" />
                {chipSuccess && <span className="verify-sphere-check">✓</span>}
                {isClickable && !chipSuccess && (
                  <span className="verify-sphere-label">Click</span>
                )}
              </motion.button>
            </div>
            {hint && !showPrimaryHint && <p className="verify-hint verify-hint--overlay">{hint}</p>}
          </>
        )}
        </div>
      </div>
    );
  }

  return (
    <div className="verification-gate-root">
      <VerificationGateBackground preferStatic={prefersReducedMotion ?? false} />
      <div className="verification-shell verification-shell--password">
      <div className="password-card verification-card">
        <div className="password-badge">Private Portfolio</div>
        <h1 className="password-title">Access Required</h1>
        <p className="password-subtitle">
          Humanity confirmed. Enter the password to view Joshua&apos;s work.
        </p>
        <form className="password-form" onSubmit={handlePasswordSubmit}>
          <label className="password-label" htmlFor="portfolio-password">
            Password
          </label>
          <input
            id="portfolio-password"
            className="password-input"
            type="password"
            value={passwordInput}
            onChange={(event) => setPasswordInput(event.target.value)}
            placeholder="Enter access code"
            autoComplete="current-password"
          />
          {passwordError && <span className="password-error">{passwordError}</span>}
          <button className="password-button" type="submit">
            Enter Portfolio
          </button>
        </form>
      </div>
      </div>
    </div>
  );
};

export default VerificationGate;
